/**
 * AllPastCyclesScreen
 *
 * Full list of all past (closed) cycles. Tapping a cycle navigates to
 * PastCycleDetailScreen; the add action opens the type-first progressive
 * historical cycle entry flow.
 */

import { useRef, useState } from "react";
import { CalendarDays, ChevronLeft, MapPin, Plus } from "lucide-react-native";
import { Pressable, ScrollView, Text, View } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { NavigationProp } from "@react-navigation/native";
import { MoonEmptyState, Row, StatusBadge } from "@louise/moon-ui";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import type { CareStackParamList, MainTabParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import {
  OUTCOME_LABEL_KEYS,
  type PastCycleListItem,
  usePastCyclesList,
} from "../hooks/usePastCyclesList";
import ScreenHeader from "../components/ScreenHeader";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { EMBER } from "../../onboarding/theme";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import { PastCycleEntryDialog } from "./components/PastCycleEntryDialog";
import {
  type CycleOutcomeResultStatus,
  type CycleType,
  useCycles,
  useRecordCycleOutcome,
} from "@src/packages/cycles";
import {
  useCreateTestResult,
  useUpdateTestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import {
  buildBetaTestResultPayload,
  buildBetaTestResultUpdate,
  PATIENT_BETA_OUTCOME_SOURCE_KIND,
} from "@src/packages/cycles/presenters/betaTestResult";
import {
  endingForBetaWrite,
  outcomeWriteTarget,
  resolveOutcomeWriteRequestId,
  shouldRecordAbandonedEnding,
  type OutcomeWriteRequestId,
} from "@src/packages/cycles/presenters/patientOutcomeWrite";
import { betaResultErrorMessage } from "./care/modals/betaResultErrorMessage";
import { createIdempotencyKey } from "@src/packages/care/cryovault/useSpecimens";
import {
  RecordBetaResultModal,
  type BetaResultPayload,
} from "./care/modals/RecordBetaResultModal";
import { toLocalDateString } from "@src/packages/utils/date";
import {
  useCanonicalCycleIntake,
  type CanonicalCycleDraft,
} from "@src/packages/care/clinical-record/useCanonicalCycleIntake";

type OutcomeDetailsCycle = {
  cycleId: string;
  cycleType: CycleType;
  /** The ending chosen in the entry dialog, recorded if the modal is abandoned. */
  outcome?: CanonicalCycleDraft["outcome"];
};

type Props = NativeStackScreenProps<CareStackParamList, "AllPastCycles">;

export default function AllPastCyclesScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("AllPastCyclesScreen");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const pastCycles = usePastCyclesList();
  const { data: allCycles } = useCycles();
  const [isAddDialogVisible, setIsAddDialogVisible] = useState(false);
  const [outcomeDetailsCycle, setOutcomeDetailsCycle] =
    useState<OutcomeDetailsCycle | null>(null);
  /**
   * The typed test result this recording created, and the value it holds. The
   * value is retained with the id because a failed outcome write leaves the
   * modal editable: a retry with a corrected number has to re-stamp that row
   * rather than link a row that still says the old one.
   */
  const [betaTestResult, setBetaTestResult] = useState<{
    id: string;
    value: number;
  } | null>(null);
  const [betaResultError, setBetaResultError] = useState<string | null>(null);
  const betaOutcomeRequestIdRef = useRef<OutcomeWriteRequestId | null>(null);
  const recordCycleOutcome = useRecordCycleOutcome();
  const createBetaTestResult = useCreateTestResult();
  const updateBetaTestResult = useUpdateTestResult();
  const { save: saveCanonicalCycle } = useCanonicalCycleIntake();

  /**
   * The record already on the cycle, read from the live cycle rather than from
   * what the entry dialog knew. A cycle the dialog reused can already carry an
   * outcome, and recording a second current record would make the API's singular
   * `outcome` view resolve to null — the past cycle would show as merely
   * "Completed" with the result the patient just entered nowhere in sight.
   */
  const existingOutcome =
    allCycles?.find((cycle) => cycle.id === outcomeDetailsCycle?.cycleId)
      ?.outcome ?? null;

  /**
   * Whether an outcome write has been issued for this hand-off.
   *
   * Set before the request, not after it resolves: a write whose response was
   * lost has still committed, and recording the handed-over ending on top of it
   * would leave the cycle with two current records and therefore no outcome at
   * all. See `shouldRecordAbandonedEnding`.
   */
  const outcomeWriteAttemptedRef = useRef(false);

  const closeBetaResult = () => {
    const abandoned = outcomeDetailsCycle;
    const attempted = outcomeWriteAttemptedRef.current;
    setOutcomeDetailsCycle(null);
    setBetaTestResult(null);
    setBetaResultError(null);
    betaOutcomeRequestIdRef.current = null;
    outcomeWriteAttemptedRef.current = false;

    /**
     * Keep the answer the patient already gave: they said how the cycle ended
     * before the modal opened, and only the beta number — optional detail they
     * can add later — is missing.
     */
    if (
      shouldRecordAbandonedEnding({
        outcomeWriteAttempted: attempted,
        hasHandedOverEnding: abandoned?.outcome != null,
        cycleAlreadyHasOutcome: existingOutcome != null,
      }) &&
      abandoned?.outcome
    ) {
      void saveCanonicalCycle({
        cycleId: abandoned.cycleId,
        outcome: abandoned.outcome,
      }).catch(() => {
        // Best effort, and silent on purpose: the modal has already closed, so
        // there is no surface left to report on. Failing here leaves exactly the
        // state this recovery was added to prevent — a saved cycle with no
        // ending — which the patient can still set from the cycle's own screen.
      });
    }
  };

  const handleConfirmBetaResult = async ({
    type,
    resultStatus,
    pregnancyOutcome,
    miscarriageOutcome,
    biochemicalPregnancyObserved,
    notes,
    betaHcgValue,
    sourceDocumentId,
  }: BetaResultPayload) => {
    if (!outcomeDetailsCycle) return;
    if (betaHcgValue == null) {
      const error = new Error("Beta hCG value is required");
      setBetaResultError(t("care.cycle_detail.alerts.error_generic"));
      throw error;
    }

    try {
      let sourceRecordId = betaTestResult?.id ?? null;
      if (!sourceRecordId) {
        const result = await createBetaTestResult.mutateAsync(
          buildBetaTestResultPayload({
            cycleId: outcomeDetailsCycle.cycleId,
            phaseId: null,
            value: betaHcgValue,
            date: toLocalDateString(new Date()),
            notes,
          }),
        );
        sourceRecordId = result.data.id;
        setBetaTestResult({ id: sourceRecordId, value: betaHcgValue });
      } else if (betaTestResult?.value !== betaHcgValue) {
        // The outcome names this row as its numeric source, so a corrected value
        // has to reach the row too — otherwise the record says 120 in the
        // outcome and still says 12 in the result it points at.
        await updateBetaTestResult.mutateAsync({
          testResultId: sourceRecordId,
          data: buildBetaTestResultUpdate({ value: betaHcgValue, notes }),
        });
        setBetaTestResult({ id: sourceRecordId, value: betaHcgValue });
      }

      // One request id per record being written. A retry with a corrected value
      // keeps it, so a first attempt whose response was lost is recognised as a
      // replay rather than stored a second time.
      const requestId = resolveOutcomeWriteRequestId(
        betaOutcomeRequestIdRef.current,
        outcomeWriteTarget({ outcomeId: existingOutcome?.id ?? null }),
        createIdempotencyKey,
      );
      betaOutcomeRequestIdRef.current = requestId;

      const isInsemination =
        outcomeDetailsCycle.cycleType === "iui" ||
        outcomeDetailsCycle.cycleType === "oi";
      // The entry dialog already asked how this cycle ended, and the modal only
      // derives a type from the number. The stated answer keeps its ending and its
      // detail; the number keeps everything a number can decide.
      const ending = endingForBetaWrite(outcomeDetailsCycle.outcome, {
        type,
        pregnancyOutcome,
        miscarriageOutcome,
      });
      // Before the request, not after: from here on the server may hold this
      // outcome whatever the client is told, so closing the modal must not write
      // the handed-over ending on top of it.
      outcomeWriteAttemptedRef.current = true;
      await recordCycleOutcome.mutateAsync({
        cycleId: outcomeDetailsCycle.cycleId,
        outcomeId: existingOutcome?.id ?? null,
        type: ending.type,
        resultStatus: resultStatus as CycleOutcomeResultStatus,
        sourceKind: PATIENT_BETA_OUTCOME_SOURCE_KIND,
        sourceRecordId,
        sourceDocumentId: sourceDocumentId ?? null,
        clientRequestId: requestId.requestId,
        procedureType: isInsemination ? "insemination" : "embryo_transfer",
        procedureModality:
          outcomeDetailsCycle.cycleType === "fet"
            ? "fet"
            : outcomeDetailsCycle.cycleType === "iui"
              ? "iui"
              : outcomeDetailsCycle.cycleType === "oi"
                ? "oi"
                : outcomeDetailsCycle.cycleType === "icsi"
                  ? "icsi"
                  : "ivf",
        procedureDate: null,
        procedureTime: null,
        pregnancyOutcome: ending.pregnancyOutcome,
        miscarriageOutcome: ending.miscarriageOutcome,
        biochemicalPregnancyObserved: biochemicalPregnancyObserved ?? null,
        notes,
        // A correction replaces the whole record, and the beta value lives in
        // the note ledger, so the earlier value has to be carried across or the
        // only place it was readable is gone.
        existingNotes: existingOutcome?.notes,
      });
      setBetaResultError(null);
    } catch (error) {
      setBetaResultError(
        betaResultErrorMessage(error, t, undefined, existingOutcome),
      );
      throw error;
    }
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 120,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={
            route.params?.returnTab
              ? t("you.shared.back")
              : `Back to ${t("care.overview.screen_title")}`
          }
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab
                  ? t("you.shared.back")
                  : t("care.overview.screen_title"),
              ),
              "back",
            );
            handleBack();
          }}
          hitSlop={8}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} strokeWidth={1.5} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {route.params?.returnTab
              ? t("you.shared.back")
              : t("care.overview.screen_title")}
          </Text>
        </Pressable>

        <ScreenHeader
          title={t("care.all_past_cycles.title")}
          subtitle={t("care.all_past_cycles.subtitle")}
          right={
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(t("care.all_past_cycles.add_button")),
                  "care.all_past_cycles.add_button",
                );
                setIsAddDialogVisible(true);
              }}
              accessibilityRole="button"
              accessibilityLabel={t("care.all_past_cycles.add_button")}
              style={({ pressed }) => ({
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: `${EMBER.colors.warm}14`,
                alignItems: "center",
                justifyContent: "center",
                opacity: pressed ? 0.65 : 1,
              })}
            >
              <Plus size={18} color={EMBER.colors.warm} strokeWidth={2} />
            </Pressable>
          }
        />

        {pastCycles.length === 0 ? (
          <View style={{ ...(GLASS_CARD_STYLE as object), padding: 24 }}>
            <MoonEmptyState title={t("care.all_past_cycles.empty")} />
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {pastCycles.map((cycle) => (
              <PastCycleRow
                key={cycle.id}
                cycle={cycle}
                onPress={() =>
                  navigation.navigate("PastCycleDetail", {
                    cycleId: cycle.id,
                  })
                }
              />
            ))}
          </View>
        )}
      </ScrollView>

      <PastCycleEntryDialog
        visible={isAddDialogVisible}
        onClose={() => setIsAddDialogVisible(false)}
        onOutcomeDetailsRequested={(cycle) => {
          setBetaTestResult(null);
          setBetaResultError(null);
          betaOutcomeRequestIdRef.current = null;
          setOutcomeDetailsCycle(cycle);
        }}
      />

      <RecordBetaResultModal
        visible={outcomeDetailsCycle != null}
        onClose={closeBetaResult}
        onConfirm={handleConfirmBetaResult}
        onComplete={closeBetaResult}
        transferDate={null}
        isLoading={
          recordCycleOutcome.isPending ||
          createBetaTestResult.isPending ||
          updateBetaTestResult.isPending
        }
        error={betaResultError}
        onOpenArticle={(articleId: string) => {
          const rootNav =
            navigation.getParent<NavigationProp<MainTabParamList>>();
          rootNav?.navigate("Learn", {
            screen: "ArticleDetail",
            params: { articleId, returnTab: "Care" },
          });
        }}
      />
    </GlassScreenLayout>
  );
}

function PastCycleRow({
  cycle,
  onPress,
}: {
  cycle: PastCycleListItem;
  onPress: () => void;
}) {
  const t = useTranslations();
  const dateLabel = cycle.startDate
    ? cycle.endDate
      ? `${cycle.startDate} — ${cycle.endDate}`
      : cycle.startDate
    : t("care.all_past_cycles.no_date");
  const clinicLabel = cycle.clinic || t("care.all_past_cycles.no_clinic");

  return (
    <Row
      accessibilityRole="button"
      accessibilityLabel={`${cycle.cycleType} #${cycle.cycleNumber}`}
      action="past_cycle_row"
      onPress={onPress}
      leading={<OrganicMiniIcon kind="process" size={38} />}
      leadingSize={38}
      title={`${cycle.cycleType} #${cycle.cycleNumber}`}
      titleRight={
        <StatusBadge
          label={t(OUTCOME_LABEL_KEYS[cycle.outcome])}
          tone={getOutcomeTone(cycle.outcome)}
        />
      }
      subtitle={
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginTop: 2,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <CalendarDays
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.6}
            />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              {dateLabel}
            </Text>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <MapPin
              size={12}
              color={EMBER.colors.textLight}
              strokeWidth={1.6}
            />
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
              numberOfLines={1}
            >
              {clinicLabel}
            </Text>
          </View>
        </View>
      }
      subtitleNumberOfLines={2}
      paddingHorizontal={16}
      paddingVertical={14}
    />
  );
}

function getOutcomeTone(
  outcome: PastCycleListItem["outcome"],
): "neutral" | "gold" | "coral" | "green" | "blue" {
  switch (outcome) {
    case "positive":
    case "completed":
      return "green";
    case "cancelled":
      return "coral";
    case "ongoing":
      return "gold";
    case "frozen":
      return "blue";
    default:
      return "neutral";
  }
}
